import { useCallback, useMemo } from "react"

import isEmpty from "lodash-es/isEmpty"
import isPlainObject from "lodash-es/isPlainObject"
import isString from "lodash-es/isString"
import omit from "lodash-es/omit"
import trim from "lodash-es/trim"

import { EStorageServiceType, SessionStorageServiceKeys, StorageService } from "@/services"

type TSeedDraftsMap = Record<string, string>

type TCoachingChatSeedDrafts = {
    read(questionId: string): string | null
    persist(questionId: string, value: string): void
    drop(questionId: string): void
    dropAll(): void
}

const sessionStorageService: StorageService = new StorageService(EStorageServiceType.Session)

/**
 * Keeps unsaved seed-question answers in session storage so a reload does not lose them.
 *
 * Seed answers only reach the server on blur, so anything typed since the last blur lives
 * nowhere but the DOM. This hook is that missing safety net.
 *
 * All drafts of one chat entity share a single storage entry (a question-uuid -> text map)
 * so that locking the seeds can wipe them with one removal, without needing the question list.
 *
 * Deliberately depends on nothing but `@/services`: it is the only state that must survive a
 * remount, and keeping it free of the redux/query graph keeps it independently testable.
 *
 * @param entityId chat entity (chapter component) the drafts belong to
 * @return TCoachingChatSeedDrafts
 */
function useCoachingChatSeedDrafts(entityId: number): TCoachingChatSeedDrafts {
    const storageKey: string = SessionStorageServiceKeys.CoachingChatSeedDrafts(entityId)

    // Storage is best-effort: Safari private mode and a full quota both throw, and neither
    // is a reason to break typing.
    const readAll: () => TSeedDraftsMap = useCallback((): TSeedDraftsMap => {
        try {
            const stored: TSeedDraftsMap | null = sessionStorageService.getItem<TSeedDraftsMap>(storageKey)

            return isPlainObject(stored) ? stored : {}
        } catch {
            return {}
        }
    }, [storageKey])

    const writeAll: (drafts: TSeedDraftsMap) => void = useCallback(
        (drafts: TSeedDraftsMap): void => {
            try {
                if (isEmpty(drafts)) sessionStorageService.removeItem(storageKey)
                else sessionStorageService.setItem<TSeedDraftsMap>(storageKey, drafts)
            } catch {
                /* storage unavailable — drafts are a convenience, never a hard requirement */
            }
        },
        [storageKey]
    )

    const read: (questionId: string) => string | null = useCallback(
        (questionId: string): string | null => {
            const draft: string | undefined = readAll()[questionId]

            return isString(draft) && !isEmpty(trim(draft)) ? draft : null
        },
        [readAll]
    )

    const drop: (questionId: string) => void = useCallback(
        (questionId: string): void => writeAll(omit<TSeedDraftsMap>(readAll(), questionId)),
        [readAll, writeAll]
    )

    const persist: (questionId: string, value: string) => void = useCallback(
        (questionId: string, value: string): void =>
            isEmpty(trim(value)) ? drop(questionId) : writeAll({ ...readAll(), [questionId]: value }),
        [drop, readAll, writeAll]
    )

    const dropAll: () => void = useCallback((): void => writeAll({}), [writeAll])

    return useMemo((): TCoachingChatSeedDrafts => ({ read, persist, drop, dropAll }), [read, persist, drop, dropAll])
}

export { useCoachingChatSeedDrafts }
export type { TCoachingChatSeedDrafts }
